import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import PropTypes from 'prop-types';
import axios from 'axios';
import '../../styles/employee/profile.css';
import TopNavbar from '../top-navbar/TopNav';
import { toast } from 'react-toastify';
import { getEmployeeProfileById, updateEmployeeMobile } from '../../services/employee/api';

const Profile = ({ loggedInUserName }) => {
    const [profileData, setProfileData] = useState({
        name: '',
        email: '',
        employeeCode: '',
        phoneNumber: '',
        employeeDepartment: '',
    });
    const [isEditingPhoneNumber, setIsEditingPhoneNumber] = useState(false);
    const [originalPhoneNumber, setOriginalPhoneNumber] = useState('');
    const [loadingProfile, setLoadingProfile] = useState(false);
    const [profileError, setProfileError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [phoneNumberError, setPhoneNumberError] = useState(null);

    useEffect(() => {
        const fetchProfileData = async () => {
            setLoadingProfile(true);
            setProfileError(null);
            setSuccessMessage(null);

            const token = localStorage.getItem('bgc_token');
            if (!token) {
                setProfileError("Authentication token not found. Please log in.");
                setLoadingProfile(false);
                return;
            }
            try {
                const response = await getEmployeeProfileById();
                const data = response.data.data;

                const mappedData = {
                    name: data.username || '',
                    email: data.email || '',
                    employeeCode: data.userid || '',
                    phoneNumber: data.phonenumber || '',
                    employeeDepartment: data.department || '',
                };

                setProfileData(mappedData);
                setOriginalPhoneNumber(mappedData.phoneNumber);
            } catch (error) {
                console.error("Error fetching profile data:", error);
                setProfileError("Failed to load profile data: " + (error.response?.data?.message || error.message));
            } finally {
                setLoadingProfile(false);
            }
        };

        fetchProfileData();
    }, []);

    const handlePhoneEditClick = () => {
        setIsEditingPhoneNumber(true);
        setOriginalPhoneNumber(profileData.phoneNumber);
        setSuccessMessage(null);
        setProfileError(null);
        setPhoneNumberError(null);
    };

    const handleProfileChange = (e) => {
        const { name, value } = e.target;
        setProfileData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    const handlePhoneSaveClick = async () => {
        setLoadingProfile(true);
        setProfileError(null);
        setPhoneNumberError(null);
        setSuccessMessage(null);

        const token = localStorage.getItem('bgc_token');
        const mobileNumber = profileData.phoneNumber;

        const mobileNumberPattern = /^[6-9]\d{9}$/;
        if (!mobileNumberPattern.test(mobileNumber)) {
            setPhoneNumberError("Please enter a valid 10-digit Indian mobile number starting with 6, 7, 8, or 9.");
            setLoadingProfile(false);
            return;
        }

        if (mobileNumber === originalPhoneNumber) {
            setPhoneNumberError("No changes detected. Please update the phone number to save.");
            setLoadingProfile(false);
            return;
        }

        try {
            const response = await updateEmployeeMobile(mobileNumber);

            if (response.status === 200) {
                setOriginalPhoneNumber(mobileNumber);
                setIsEditingPhoneNumber(false);
                toast.success("Profile Updated Successfully", {
                                autoClose: 3000, // 3 seconds
                                });
            } else {
                throw new Error(response.data?.message || `Failed to update profile: ${response.status}`);
            }

        } catch (error) {
            console.error("Error saving profile data:", error);
            setProfileError("Failed to save profile: " + (error.response?.data?.message || error.message));
        } finally {
            setLoadingProfile(false);
        }
    };

    const handlePhoneCancelClick = () => {
        setProfileData(prevData => ({
            ...prevData,
            phoneNumber: originalPhoneNumber
        }));
        setIsEditingPhoneNumber(false);
        setPhoneNumberError(null);
    };

    return (
        <div className="emp-profile-dashboard-app-container">
            <Sidebar />
            <div className="emp-profile-dashboard-right-panel">
                <TopNavbar />
                <div className="emp-profile-info-container">
                    <div className="emp-profile-info-header">
                        <h2>Profile Information</h2>
                    </div>
                    <div className="emp-profile-form-card">
                        {loadingProfile &&  <div className="centered-message">
    <div className="spinner"></div>
    <div>Loading profile data...</div>
  </div>}
                        {profileError && <p className="emp-profile-error-message">{profileError}</p>}
                        {successMessage && <p className="emp-profile-success-message">{successMessage}</p>}

                        {!loadingProfile && !profileError && (
                            <>
                                <div className="emp-profile-form-group">
                                    <label className="emp-profile-form-label">Name</label>
                                    <input
                                        type="text"
                                        className="emp-profile-form-input emp-profile-disabled-field"
                                        name="name"
                                        value={profileData.name}
                                        readOnly={true}
                                    />
                                </div>
                                <div className="emp-profile-form-group">
                                    <label className="emp-profile-form-label">Email</label>
                                    <input
                                        type="email"
                                        className="emp-profile-form-input emp-profile-disabled-field"
                                        name="email"
                                        value={profileData.email}
                                        readOnly={true}
                                    />
                                </div>
                                <div className="emp-profile-form-group">
                                    <label className="emp-profile-form-label">Employee Code</label>
                                    <input
                                        type="text"
                                        className="emp-profile-form-input emp-profile-disabled-field"
                                        name="employeeCode"
                                        value='CS741'
                                        readOnly={true}
                                    />
                                </div>
                                <div className="emp-profile-form-group">
                                    <label className="emp-profile-form-label">Phone Number</label>
                                    <div className="emp-profile-phone-input-wrapper">
                                        <input
                                            type="text"
                                            className={`emp-profile-form-input ${!isEditingPhoneNumber ? 'emp-profile-disabled-field' : ''}`}
                                            name="phoneNumber"
                                            value={profileData.phoneNumber}
                                            readOnly={!isEditingPhoneNumber}
                                            onChange={handleProfileChange}
                                        />
                                        {!isEditingPhoneNumber ? (
                                            <button className="emp-profile-field-edit-button" onClick={handlePhoneEditClick}>
                                                Update
                                            </button>
                                        ) : (
                                            <div className="emp-profile-field-buttons">
                                                <button className="emp-profile-field-cancel-button" onClick={handlePhoneCancelClick}>
                                                    Cancel
                                                </button>
                                                <button className="emp-profile-field-save-button" onClick={handlePhoneSaveClick}>
                                                    Save
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    {phoneNumberError && <p className="emp-profile-field-error-message">{phoneNumberError}</p>}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

Profile.propTypes = {
    loggedInUserName: PropTypes.string.isRequired,
};

export default Profile;